import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { MessageMarkdown } from "@/components/chat/ChatMessage";
import { compactMarkdownComponents } from "@/components/common/markdown-components";
import { IconButton } from "@/components/common/IconButton";
import { NavItemButton } from "@/components/common/NavItemButton";
import { Spinner } from "@/components/common/Spinner";
import { isImeConsumedKeyDown } from "@/lib/ime-composition";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useHelpAgentStore } from "@/stores/useHelpAgentStore";

/** サーバーが受け付けるユーザー発言の最大文字数。 */
export const HELP_AGENT_MAX_INPUT_LENGTH = 2000;

const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 520;
const EDGE_OFFSET = 8;
/** トースト (1000) より下に置く。 */
const Z_INDEX = 900;

interface HelpAgentLauncherProps {
	collapsed: boolean;
	/** パネルを開く位置。サイドバーの右端に合わせる。 */
	panelLeft: number;
}

/** サイドバーに置く、アプリの使い方を質問できるヘルプ。パネルはサイドバーの右に開く。 */
export function HelpAgentLauncher({ collapsed, panelLeft }: HelpAgentLauncherProps) {
	const organizationId = useCurrentUserStore((s) => s.organizationId);
	const helpAgentAvailable = useCurrentUserStore((s) => s.helpAgentAvailable);
	const isOpen = useHelpAgentStore((s) => s.isOpen);
	const toggle = useHelpAgentStore((s) => s.toggle);

	if (organizationId === "" || !helpAgentAvailable) return null;

	return (
		<>
			<NavItemButton
				icon="helpBubble"
				label="ヘルプ"
				collapsed={collapsed}
				selected={isOpen}
				onClick={toggle}
				testId="help-agent-launcher"
			/>
			{isOpen && <HelpAgentPanel left={panelLeft} />}
		</>
	);
}

function HelpAgentPanel({ left }: { left: number }) {
	const { pathname } = useLocation();
	const creditExhausted = useCurrentUserStore((s) => s.creditExhausted);
	const messages = useHelpAgentStore((s) => s.messages);
	const pending = useHelpAgentStore((s) => s.pending);
	const error = useHelpAgentStore((s) => s.error);
	const send = useHelpAgentStore((s) => s.send);
	const reset = useHelpAgentStore((s) => s.reset);
	const toggle = useHelpAgentStore((s) => s.toggle);
	const input = useHelpAgentStore((s) => s.draft);
	const setInput = useHelpAgentStore((s) => s.setDraft);
	const listRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const list = listRef.current;
		if (list) list.scrollTop = list.scrollHeight;
	}, [messages, pending]);

	const tooLong = input.length > HELP_AGENT_MAX_INPUT_LENGTH;
	const canSend = !creditExhausted && !pending && input.trim() !== "" && !tooLong;

	const submit = () => {
		if (!canSend) return;
		const text = input.trim();
		setInput("");
		void send(text, pathname).catch((err: unknown) => {
			console.error("ヘルプエージェントへの質問の送信に失敗しました", err);
		});
	};

	return (
		<div
			data-testid="help-agent-panel"
			className="flex flex-col overflow-hidden rounded-4 bg-surface-1 shadow-popover"
			style={{
				position: "fixed",
				left: left + EDGE_OFFSET,
				bottom: EDGE_OFFSET,
				zIndex: Z_INDEX,
				width: PANEL_WIDTH,
				height: PANEL_HEIGHT,
				maxHeight: `calc(100vh - ${EDGE_OFFSET * 2}px)`,
			}}
		>
			<div className="flex items-center gap-2 border-b border-line-1 px-3 py-2">
				<span className="flex-1 text-ui font-semibold text-fg-1">Izanagi ヘルプ</span>
				<IconButton
					icon="plus"
					data-testid="help-agent-reset"
					title="新しい会話を始める"
					disabled={pending || messages.length === 0}
					onClick={reset}
				/>
				<IconButton icon="x" data-testid="help-agent-close" onClick={toggle} />
			</div>

			<div ref={listRef} className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
				{messages.length === 0 && (
					<p className="m-0 text-caption leading-body text-fg-3">
						Izanagi の画面や操作の使い方について質問できます。
					</p>
				)}
				{messages.map((m, i) =>
					m.role === "user" ? (
						<div
							key={i}
							data-testid="help-agent-user-message"
							className="max-w-[85%] self-end whitespace-pre-wrap break-words rounded-3 bg-surface-2 px-2.5 py-1.5 text-caption leading-body text-fg-1"
						>
							{m.text}
						</div>
					) : (
						<div
							key={i}
							data-testid="help-agent-assistant-message"
							className="select-text break-words text-caption leading-body text-fg-1"
						>
							<MessageMarkdown text={m.text} components={compactMarkdownComponents} />
						</div>
					),
				)}
				{pending && <Spinner data-testid="help-agent-pending" />}
				{error && (
					<p data-testid="help-agent-error" className="m-0 text-caption text-danger">
						{error}
					</p>
				)}
			</div>

			<div className="px-3 py-2">
				{creditExhausted ? (
					<p data-testid="help-agent-credit-exhausted" className="m-0 text-caption text-danger">
						組織のクレジット残高が不足しているため、質問できません。
					</p>
				) : (
					<>
						<div className="flex items-end gap-2 rounded-4 bg-surface-1 py-1.5 pr-1.5 pl-2 shadow-card">
							<textarea
								data-testid="help-agent-input"
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key !== "Enter" || e.shiftKey || isImeConsumedKeyDown(e.nativeEvent))
										return;
									e.preventDefault();
									submit();
								}}
								placeholder="使い方を質問する"
								rows={2}
								className="flex-1 resize-none border-0 bg-transparent p-1 font-[inherit] text-caption leading-body text-fg-1 outline-none"
							/>
							{/* 色はメインチャットの送信ボタン (ComposerInput) に揃える。 */}
							<IconButton
								icon="arrowU"
								size={28}
								data-testid="help-agent-send"
								disabled={!canSend}
								onClick={submit}
								color={canSend ? "var(--on-accent-solid)" : "var(--fg-4)"}
								background={canSend ? "var(--accent-solid)" : "var(--neutral-solid)"}
								hoverBackground="var(--accent-solid-hover)"
								style={{
									borderRadius: "var(--radius-full)",
									cursor: canSend ? "pointer" : "not-allowed",
									opacity: 1,
									flexShrink: 0,
								}}
							/>
						</div>
						{tooLong && (
							<p data-testid="help-agent-too-long" className="m-0 mt-1 text-caption text-danger">
								質問は {HELP_AGENT_MAX_INPUT_LENGTH.toLocaleString()} 文字以内にしてください。
							</p>
						)}
					</>
				)}
			</div>
		</div>
	);
}
